import { useState, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Box, Grid, TextField } from '@mui/material'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import InputField from 'components/form/InputField'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import ChallengeTypeSelect from 'components/form/ChallengeTypeSelect'
import SwitchToggle from 'components/form/SwitchToggle'
import DateTimePicker from 'components/form/DateTimePicker'
import GenericSelect from 'components/form/GenericSelect'
import InputErrorMessage from 'components/ui/InputErrorMessage'
import { selectCountriesWithClubs } from 'store/countries/slice'
import { searchClubs } from 'store/clubs/thunks'
import { selectClubs } from 'store/clubs/slice'
import { convertCountryCodesToCountries, convertCountryIdsToCountries } from 'utils/convertCountryCodesToCountries'
import { selectChallengeTypes } from 'store/challengeTypes/slice'
import { updateChallengeInfo } from 'store/challenge/slice'
import { debounce } from 'utils/debounce'
import { Controller, UseFormReturn } from 'react-hook-form'
import {
  ChallengeCountType,
  getCountTypesForChallengeType,
  ChallengeComputedState,
  GolfRoundType,
  getDefaultMinPar,
} from 'utils/challenges'
import TimeZoneSelect from '../TimeZoneSelect'
import { UserSubscriptionType } from 'utils/users'
import { TIMEZONE_ID_HELSINKI } from 'utils/timeZone'
import SearchableClubSelect from '../SearchableClubSelect'
import SearchableCountrySelect from '../SearchableCountrySelect'
import SearchableCourseSelect from '../SearchableCourseSelect'
import { ChallengeTypeConfig } from 'api/types'

interface Props {
  form: UseFormReturn<any>
  challenge: Challenge
  // Redux replaces `clubs` on every new search and this component remounts on every tab change,
  // so the club data we have already seen is kept by the form above us. It is what resolves the
  // courses of clubs that were selected in an earlier search.
  clubsById: Record<number, Club>
}

export default function ChallengeFormPhase1({ form, challenge, clubsById }: Props): JSX.Element {
  const intl = useIntl()
  const { register, control, setValue, watch, getValues } = form
  const dispatch = useDispatch()

  const [initialClubs, setInitialClubs] = useState<SelectOption[]>([])

  const [initialClubsAdded, setInitialClubsAdded] = useState(false)

  const { countries, status: countryStatus } = useSelector(selectCountriesWithClubs)
  const { clubs, status: clubStatus } = useSelector(selectClubs)
  const { types: challengeTypes } = useSelector(selectChallengeTypes)
  const selectedCountries = watch('roundEligibility.countries')
  const selectedClubIds = watch('roundEligibility.clubIds')
  const selectedCourseIds = watch('roundEligibility.courseIds')
  const maxLength = 60
  // The include/exclude dropdown stores its selection as the 'true' / 'false' option key, so the
  // raw form value is a string that would read as truthy in exclude mode.
  const locationCountryIsIncluded =
    String(watch('roundEligibility.countriesInclusive', challenge.roundEligibility?.countriesInclusive ?? true)) !==
    'false'
  const roundTypes: GolfRoundType[] | undefined = watch(
    'roundEligibility.roundTypes',
    challenge.roundEligibility?.roundTypes ?? [],
  )
  const joinAudience: ChallengeAudience = watch('audienceControl.joinAudience')
  const calculationType = watch('calculationType')
  const challengeType = watch('typeConfig.type')
  const previousChallengeType = useRef(challengeType)
  const isEclecticChallenge = challengeType === ChallengeTypeConfig.ECLECTIC
  const isHcpRoundsOnly =
    !!roundTypes?.length &&
    roundTypes.every(
      (type) => type === GolfRoundType.HCP_COMPETITION_PLAY_ROUND || type === GolfRoundType.HCP_GENERAL_PLAY_ROUND,
    )

  const isSimulatorRoundsOnly =
    !!roundTypes?.length && roundTypes.every((type) => type === GolfRoundType.SIMULATOR_ROUND)

  const isGoldChallenge =
    (joinAudience?.userSubscriptionTypes?.length ?? 0) > 0 &&
    (joinAudience?.userSubscriptionTypes?.every((type) => type === UserSubscriptionType.GOLD) ?? false)

  // Disable editing settings
  const maybeLockSettings = useMemo(() => {
    const hasChallengeStarted =
      challenge?.schedule?.startAt && new Date(challenge.schedule.startAt).getTime() <= Date.now()

    if (challenge.computedState === ChallengeComputedState.UNPUBLISHED && !hasChallengeStarted) {
      return false
    }

    if (
      challenge.computedState === ChallengeComputedState.LIVE ||
      challenge.computedState === ChallengeComputedState.COMPLETED ||
      challenge.computedState === ChallengeComputedState.UNPUBLISHED ||
      form.formState.disabled
    ) {
      return true
    }
    return false
  }, [challenge.computedState, challenge.schedule?.startAt, form.formState.disabled])

  // A completed challenge, or a form that has been disabled altogether, is read only.
  const locationsAreReadOnly = challenge.computedState === ChallengeComputedState.COMPLETED || !!form.formState.disabled

  // An ongoing challenge may only ever get less restrictive, because narrowing the eligibility
  // would disqualify rounds that have already been played. An include list (allowlist) can still
  // grow but has to keep everything it was saved with, an exclude list (blocklist) works the other
  // way round: saved entries can go, new ones cannot be added.
  const challengeHasStarted =
    !!challenge?.schedule?.startAt && new Date(challenge.schedule.startAt).getTime() <= Date.now()
  const challengeIsActive = challenge.computedState === ChallengeComputedState.LIVE || challengeHasStarted
  const disableRemovingSavedLocations =
    challengeIsActive && locationCountryIsIncluded && challenge.computedState !== ChallengeComputedState.DRAFT
  const disableAddingLocations =
    locationsAreReadOnly ||
    (challengeIsActive && !locationCountryIsIncluded && challenge.computedState !== ChallengeComputedState.DRAFT)

  const savedCountryKeys = useMemo(
    () =>
      convertCountryCodesToCountries(countries, challenge.roundEligibility?.countries ?? []).map((country: Country) =>
        String(country.id),
      ),
    [countries, challenge.roundEligibility?.countries],
  )

  const savedClubKeys = useMemo(
    () => (challenge.roundEligibility?.clubIds ?? []).map(String),
    [challenge.roundEligibility?.clubIds],
  )

  const savedCourseKeys = useMemo(
    () => (challenge.roundEligibility?.courseIds ?? []).map(String),
    [challenge.roundEligibility?.courseIds],
  )

  useEffect(() => {
    if (clubStatus === 'succeeded' && initialClubs?.length === 0 && !initialClubsAdded && selectedClubIds?.length > 0) {
      const selectedClubsData = clubs.filter((club: Club) => {
        return selectedClubIds.some((selected: SelectOption) => selected.key === club.id)
      })

      const initialClubsValue = selectedClubsData.map((club: Club) => {
        return { key: club.id, label: club.name }
      })

      setInitialClubs(initialClubsValue)
      setInitialClubsAdded(true)
    }
  }, [clubStatus, selectedClubIds, clubs, initialClubs, initialClubsAdded])

  const countriesOptions: SelectOption[] = useMemo(
    () =>
      countries.map((country: Country) => {
        return { key: country.id, label: country.name }
      }),
    [countries],
  )

  const clubsOptions: SelectOption[] = clubs.map((club: Club) => {
    return { key: club.id, label: club.name }
  })

  const coursesOptions: SelectOption[] = useMemo(() => {
    const selectedCourseKeys = new Set(
      ((selectedCourseIds ?? []) as SelectOption[]).map((selected: SelectOption) => String(selected.key)),
    )

    return ((selectedClubIds ?? []) as SelectOption[])
      .flatMap((selected: SelectOption) => clubsById[Number(selected.key)]?.courses ?? [])
      .filter((course: Course) => course.isOfficial || selectedCourseKeys.has(String(course.id)))
      .map((course: Course) => {
        return {
          key: course.id,
          label: course.name,
        }
      })
  }, [clubsById, selectedClubIds, selectedCourseIds])

  const calculationTypeOptions: SelectOption[] = [
    { key: ChallengeCountType.TOTAL, label: intl.formatMessage({ id: 'strings.total' }) },
    { key: ChallengeCountType.BEST, label: intl.formatMessage({ id: 'strings.best' }) },
  ]

  const includesSelectOptions: SelectOption[] = useMemo(
    () => [
      { key: 'true', label: intl.formatMessage({ id: 'strings.includes' }) },
      { key: 'false', label: intl.formatMessage({ id: 'strings.excludes' }) },
    ],
    [intl],
  )

  const allowedCalculationTypes = useMemo(() => {
    return getCountTypesForChallengeType(challengeType)
  }, [challengeType])

  const getCalculationTypeValue = useMemo(() => {
    const isAllowedCalculationType = allowedCalculationTypes?.includes(calculationType)

    if (!isAllowedCalculationType && calculationType !== '' && challengeTypes.length > 0) {
      setValue('calculationType', '')
    }

    return calculationType
  }, [watch, setValue, challengeTypes, allowedCalculationTypes, calculationType])

  const getCalculationTypeOptions = useMemo(() => {
    return calculationTypeOptions.filter((option) =>
      allowedCalculationTypes?.includes(option.key as ChallengeCountType),
    )
  }, [calculationTypeOptions, allowedCalculationTypes])

  useEffect(() => {
    if (isEclecticChallenge) {
      setValue('scoringType', '')
      setValue('targetCount', '')
    }
  }, [isEclecticChallenge, setValue])

  useEffect(() => {
    if (previousChallengeType.current === challengeType && !getValues('roundEligibility.minCoursePar')) {
      setValue(
        'roundEligibility.minCoursePar',
        challenge.roundEligibility?.minCoursePar || getDefaultMinPar(challengeType),
      )
      return
    } else if (previousChallengeType.current === challengeType) {
      return
    }
    previousChallengeType.current = challengeType

    const savedMinCoursePar = challenge.roundEligibility?.minCoursePar
    const minCoursePar =
      challengeType === challenge.typeConfig?.type && savedMinCoursePar
        ? savedMinCoursePar
        : getDefaultMinPar(challengeType)

    setValue('roundEligibility.minCoursePar', minCoursePar, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }, [challengeType, challenge.roundEligibility?.minCoursePar, challenge.typeConfig?.type, getValues, setValue])

  useEffect(() => {
    if (challenge.roundEligibility?.countriesInclusive && locationCountryIsIncluded) {
      return
    }
    setValue('roundEligibility.clubIds', [])
    setValue('roundEligibility.courseIds', [])
  }, [locationCountryIsIncluded])

  const searchForClubs = debounce((searchString: string | undefined) => {
    if (searchString && searchString.length >= 3) {
      const countryIds = convertCountryIdsToCountries(
        countries,
        selectedCountries.map((c) => c.key),
      ).map((country) => country.id)

      // v1 search expects country id as numbers but v2 response gives country code
      dispatch(
        searchClubs({
          data: {
            countryIds,
            clubName: searchString,
            activeClubs: true,
            activeCourses: true,
          },
        }),
      )
    }
  }, 500)

  const getScoringOptions = () => {
    return [
      { key: 'allOnePoint', label: intl.formatMessage({ id: 'birdiesOrBetter.allOnePoint' }) },
      { key: 'pointLadder', label: intl.formatMessage({ id: 'birdiesOrBetter.pointLadder' }) },
    ]
  }

  const getStrokeplayScoringOptions = () => {
    return [
      { key: 'net', label: intl.formatMessage({ id: 'challengeUnits.net_stroke' }) },
      { key: 'gross', label: intl.formatMessage({ id: 'challengeUnits.highest_stroke' }) },
    ]
  }

  const setStrokeplayScoringType = (value: any) => {
    setValue('typeConfig.strokeplay', {
      useHandicap: value === 'net',
    })
  }

  const setBirdiesOrBetterScoringType = (value: any) => {
    if (value === 'allOnePoint') {
      setValue('typeConfig.birdiesOrBetter', {
        birdieValue: 1,
        eagleValue: 1,
        hioValue: 1,
        albatrossValue: 1,
      })
    } else if (value === 'pointLadder') {
      setValue('typeConfig.birdiesOrBetter', {
        birdieValue: 1,
        eagleValue: 3,
        hioValue: 6,
        albatrossValue: 10,
      })
    }
  }

  return (
    <>
      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'challenges.challengeTypeSettings' })}</h2>
          </Grid>
          <Grid
            item
            xs={4}
            sx={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <ChallengeLabel>{intl.formatMessage({ id: 'strings.challengeName' })}*</ChallengeLabel>
            <Controller
              name="info.name"
              control={control}
              defaultValue={challenge?.info?.name || ''}
              render={({ field, fieldState }) => {
                const currentLength = field.value?.length ?? 0
                return (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      const name = e.target.value.normalize('NFKC')
                      field.onChange(name)
                      dispatch(updateChallengeInfo({ name }))
                    }}
                    variant="outlined"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      <Box
                        component={'span'}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          flex: '1',
                        }}
                      >
                        <span>{fieldState.error?.message}</span>
                        <span>
                          {currentLength}/{maxLength}
                        </span>
                      </Box>
                    }
                    inputProps={{
                      maxLength,
                    }}
                    placeholder={intl.formatMessage({ id: 'strings.challengeName' })}
                    FormHelperTextProps={{
                      sx: {
                        marginLeft: 0,
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',

                        '& fieldset': {
                          borderColor: '#e4e4e7',
                        },

                        '&:hover fieldset': {
                          borderColor: '#e4e4e7',
                        },

                        '&.Mui-focused fieldset': {
                          borderColor: '#e4e4e7',
                        },
                      },

                      '& .MuiOutlinedInput-input': {
                        padding: '10px 12px',
                      },
                    }}
                  />
                )
              }}
            />
          </Grid>
          <Grid item xs={8} sx={{ alignItems: 'center', alignContent: 'center', display: 'flex' }}>
            <Grid container gap={'24px'}>
              <Grid item xs={'auto'}>
                <HcpRoundsOnly
                  name="hcpRound"
                  label={intl.formatMessage({ id: 'strings.hcpRoundsOnly' })}
                  labelPlacement="end"
                  switchStyles={{ left: '0' }}
                  value={isHcpRoundsOnly}
                  onChange={(e) => {
                    const checked = e.target.checked
                    if (
                      checked &&
                      !roundTypes?.includes(GolfRoundType.HCP_COMPETITION_PLAY_ROUND) &&
                      !roundTypes?.includes(GolfRoundType.HCP_GENERAL_PLAY_ROUND)
                    ) {
                      setValue('roundEligibility.roundTypes', [
                        ...(getValues('roundEligibility.roundTypes') || []).filter(
                          (type: GolfRoundType) => type !== GolfRoundType.SIMULATOR_ROUND,
                        ),
                        GolfRoundType.HCP_COMPETITION_PLAY_ROUND,
                        GolfRoundType.HCP_GENERAL_PLAY_ROUND,
                      ])
                    } else {
                      setValue('roundEligibility.roundTypes', [
                        ...(getValues('roundEligibility.roundTypes') || []).filter(
                          (type: GolfRoundType) =>
                            type !== GolfRoundType.HCP_COMPETITION_PLAY_ROUND &&
                            type !== GolfRoundType.HCP_GENERAL_PLAY_ROUND,
                        ),
                      ])
                    }
                  }}
                  disabled={maybeLockSettings}
                />
              </Grid>
              <Grid item xs={'auto'}>
                <SimulatorRoundsOnly
                  name="simulatorRound"
                  label={intl.formatMessage({ id: 'strings.simulatorRoundsOnly' })}
                  value={isSimulatorRoundsOnly}
                  labelPlacement="end"
                  switchStyles={{ left: '0' }}
                  onChange={(e) => {
                    const checked = e.target.checked
                    if (checked && !roundTypes?.includes(GolfRoundType.SIMULATOR_ROUND)) {
                      setValue('roundEligibility.roundTypes', [
                        ...(getValues('roundEligibility.roundTypes') || []).filter(
                          (type: GolfRoundType) =>
                            type !== GolfRoundType.HCP_COMPETITION_PLAY_ROUND &&
                            type !== GolfRoundType.HCP_GENERAL_PLAY_ROUND,
                        ),
                        GolfRoundType.SIMULATOR_ROUND,
                      ])
                    } else {
                      setValue('roundEligibility.roundTypes', [
                        ...(getValues('roundEligibility.roundTypes') || []).filter(
                          (type: GolfRoundType) => type !== GolfRoundType.SIMULATOR_ROUND,
                        ),
                      ])
                    }
                  }}
                  disabled={maybeLockSettings}
                />
              </Grid>
              <Grid item xs={'auto'}>
                <GoldChallenge
                  name="goldChallenge"
                  label={intl.formatMessage({ id: 'strings.goldChallenge' })}
                  value={isGoldChallenge}
                  labelPlacement="end"
                  switchStyles={{ left: '0' }}
                  onChange={(e) => {
                    const checked = e.target.checked
                    if (checked && !joinAudience?.userSubscriptionTypes?.includes(UserSubscriptionType.GOLD)) {
                      setValue('audienceControl.joinAudience.userSubscriptionTypes', [
                        ...(getValues('audienceControl.joinAudience.userSubscriptionTypes') || []),
                        UserSubscriptionType.GOLD,
                      ])
                    } else if (!checked) {
                      setValue('audienceControl.joinAudience.userSubscriptionTypes', [
                        ...(getValues('audienceControl.joinAudience.userSubscriptionTypes') || []).filter(
                          (type: UserSubscriptionType) => type !== UserSubscriptionType.GOLD,
                        ),
                      ])
                    }
                    if (checked && !joinAudience.privileges?.includes('USER_PRIVILEGE_GOLD_MEMBERSHIP')) {
                      setValue('audienceControl.extraViewAudience.userSubscriptionTypes', [
                        ...(getValues('audienceControl.extraViewAudience.userSubscriptionTypes') || []),
                        UserSubscriptionType.FREE,
                      ])
                    }
                    setValue('audienceControl.useJoinAudienceOnly', !checked)
                  }}
                  disabled={maybeLockSettings}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={
              challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ||
              challengeType === ChallengeTypeConfig.STROKEPLAY
                ? 3
                : 4
            }
          >
            <ChallengeTypeSelect
              name="typeConfig.type"
              control={control}
              value={challengeType}
              disabled={maybeLockSettings}
            />
          </Grid>
          {challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER && (
            <Grid item xs={3} style={{ marginTop: '10px' }}>
              <StyledGenericSelect
                name="scoringType"
                label={intl.formatMessage({ id: 'strings.scoring' })}
                control={control}
                options={getScoringOptions()}
                required
                syncChange={setBirdiesOrBetterScoringType}
                disabled={maybeLockSettings}
                placeholder="Select"
              />
            </Grid>
          )}

          {!isEclecticChallenge && challengeType === ChallengeTypeConfig.STROKEPLAY && (
            <Grid item xs={3} style={{ marginTop: '10px' }}>
              <StyledGenericSelect
                name="scoringType"
                label={intl.formatMessage({ id: 'strings.scoring' })}
                control={control}
                options={getStrokeplayScoringOptions()}
                required
                syncChange={setStrokeplayScoringType}
                disabled={maybeLockSettings}
                placeholder="Select"
              />
            </Grid>
          )}
          {!isEclecticChallenge && (
            <Grid
              item
              xs={
                challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ||
                challengeType === ChallengeTypeConfig.STROKEPLAY
                  ? 3
                  : 4
              }
              style={{ marginTop: '10px' }}
            >
              <StyledGenericSelect
                name="calculationType"
                label={intl.formatMessage({ id: 'strings.count' })}
                control={control}
                options={getCalculationTypeOptions}
                initialValue={getCalculationTypeValue}
                required
                disabled={maybeLockSettings}
                placeholder="Select"
              />
            </Grid>
          )}
          {!isEclecticChallenge && (
            <Grid
              item
              xs={
                challengeType === ChallengeTypeConfig.BIRDIES_OR_BETTER ||
                challengeType === ChallengeTypeConfig.STROKEPLAY
                  ? 3
                  : 4
              }
            >
              <StyledInputfield
                register={register}
                name="targetCount"
                type="number"
                label={intl.formatMessage({ id: 'challenges.target' })}
                outlined
                disabled={maybeLockSettings}
                placeholder="Enter"
              />
            </Grid>
          )}
        </Grid>
      </FormSectionContainer>

      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'strings.locationChallengeCanBePerformed' })}</h2>
          </Grid>
          <Grid item xs={4}>
            <StyledGenericSelect
              name="roundEligibility.countriesInclusive"
              label={intl.formatMessage({ id: 'challenges.type' })}
              control={control}
              options={includesSelectOptions}
              disabled={maybeLockSettings}
              showResetOption={false}
            />
          </Grid>
          <Grid item xs={4}>
            <CoursePar
              register={register}
              name="roundEligibility.minCoursePar"
              type="number"
              label={intl.formatMessage({ id: 'strings.minCoursePar' })}
              outlined
              value={watch('roundEligibility.minCoursePar')}
              disabled={maybeLockSettings}
              inputProps={{
                min: 0,
                max: 99,
              }}
            />
          </Grid>
          <Grid item xs={4}></Grid>

          <Grid item xs={4}>
            <SearchableCountrySelect
              name="roundEligibility.countries"
              label={intl.formatMessage({ id: 'strings.countries' })}
              form={form}
              options={countriesOptions}
              status={countryStatus}
              confirmRemoveMessage={
                challenge?.computedState === ChallengeComputedState.LIVE
                  ? intl.formatMessage({ id: 'challenges.confirmCountryRemove' })
                  : undefined
              }
              disabled={locationsAreReadOnly}
              lockedKeys={savedCountryKeys}
              disableRemove={disableRemovingSavedLocations}
              disableAdd={disableAddingLocations}
            />
          </Grid>
          <Grid item xs={4}>
            <SearchableClubSelect
              name="roundEligibility.clubIds"
              label={intl.formatMessage({ id: 'strings.clubs' })}
              form={form}
              options={clubsOptions}
              status={clubStatus}
              {...(initialClubs.length > 0 ? { initialValue: initialClubs } : {})}
              confirmRemoveMessage={
                challenge?.computedState === ChallengeComputedState.LIVE
                  ? intl.formatMessage({ id: 'challenges.confirmClubRemove' })
                  : undefined
              }
              search={searchForClubs}
              disabled={locationsAreReadOnly || watch('roundEligibility.countriesInclusive') === 'false'}
              lockedKeys={savedClubKeys}
              disableRemove={disableRemovingSavedLocations}
              disableAdd={disableAddingLocations}
            />
          </Grid>
          <Grid item xs={4}>
            <SearchableCourseSelect
              name="roundEligibility.courseIds"
              label={intl.formatMessage({ id: 'strings.courses' })}
              form={form}
              options={coursesOptions}
              status={'idle'}
              confirmRemoveMessage={
                challenge?.computedState === ChallengeComputedState.LIVE
                  ? intl.formatMessage({ id: 'challenges.confirmCourseRemove' })
                  : undefined
              }
              disabled={locationsAreReadOnly || watch('roundEligibility.countriesInclusive') === 'false'}
              lockedKeys={savedCourseKeys}
              disableRemove={disableRemovingSavedLocations}
              disableAdd={disableAddingLocations}
            />
          </Grid>
        </Grid>
      </FormSectionContainer>

      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>{intl.formatMessage({ id: 'strings.scheduling' })}</h2>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledPicker
              register={register}
              name="schedule.visibleAt"
              label={intl.formatMessage({ id: 'strings.visible' })}
              required
              style={{ width: '100%' }}
              disabled={maybeLockSettings}
              showSeconds={true}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledPicker
              register={register}
              name="schedule.startAt"
              label={intl.formatMessage({ id: 'challenges.startTime' })}
              required
              style={{ width: '100%' }}
              disabled={maybeLockSettings}
              showSeconds={true}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledPicker
              register={register}
              name="schedule.endAt"
              label={intl.formatMessage({ id: 'challenges.endTime' })}
              min={watch('schedule.startAt')}
              required
              style={{ width: '100%' }}
              showSeconds={true}
              disabled={form.formState.disabled}
            />
            {watch('schedule.endAt') && watch('schedule.startAt') > watch('schedule.endAt') && (
              <InputErrorMessage>
                {intl.formatMessage({ id: 'strings.endTimeShouldBeGreaterThanStartTime' })}
              </InputErrorMessage>
            )}
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <TimeZoneSelect
              name="metadata.timeZoneId"
              control={control}
              value={watch('metadata.timeZoneId') ?? TIMEZONE_ID_HELSINKI}
              required
              style={{ marginBottom: '0' }}
              disabled={maybeLockSettings}
            />
          </Grid>
        </Grid>
      </FormSectionContainer>
    </>
  )
}

const HcpRoundsOnly = styled(SwitchToggle)``

const SimulatorRoundsOnly = styled(HcpRoundsOnly)``

const GoldChallenge = styled(HcpRoundsOnly)``

const ChallengeLabel = styled.div`
  color: var(--secondary-foreground, #373d40);

  /* Text-sm/Medium */
  font-family: var(--family-sans, Söhne);
  font-size: var(--size-sm, 14px);
  font-style: normal;
  font-weight: var(--weight-medium, 500);
  line-height: var(--leading-5, 20px); /* 142.857% */
  letter-spacing: var(--tracking-normal, 0);
`

const StyledGenericSelect = styled(GenericSelect)`
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`

const StyledInputfield = styled(InputField)`
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`

const CoursePar = styled(InputField)`
  margin: 10px 0 !important;
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`
const StyledPicker = styled(DateTimePicker)`
  margin-top: 15px;

  & div:before,
  .MuiInput-underline:hover:before {
    border-bottom: none;
  }

  & .MuiInput-underline:hover {
    & input {
      border-color: #e4e4e7;
    }
  }

  & input {
    border: 1px solid;
    border-color: #e4e4e7;
    border-radius: 8px;
    padding: 16px 14px;
  }
  & label {
    transition: none;
    transform: none;
    margin-top: -20px;
  }

  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }
`
